/**
 * Phase 2B — AutosaveIndicator + SaveHistorySheet + RecoveryDialog.
 *
 * Covers founder refinements R1, R4, and the surfacing of R2 + R5.
 */
import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Cloud, CloudOff, CheckCircle2, AlertTriangle, Loader2, History,
  RotateCcw, X, Save, Timer, GitBranch,
} from 'lucide-react';
import { formatDistanceToNow, format } from 'date-fns';
import {
  Sheet, SheetContent, SheetHeader, SheetTitle, SheetTrigger,
} from '@/components/ui/sheet';
import {
  Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Card } from '@/components/ui/card';
import { adminApi } from '@/lib/adminApi';
import { formatApiError } from '@/lib/api';
import { toast } from 'sonner';

/* ============================================================================
 *                    AUTOSAVE INDICATOR (R1 + R4)
 * ========================================================================== */

export function AutosaveIndicator({ state, lastSavedAt, lastError, onRetry, onOpenHistory }) {
  const { t } = useTranslation();
  const config = STATE_CONFIG[state] || STATE_CONFIG.idle;
  const Icon = config.icon;
  const timeAgo = lastSavedAt ? safeTimeAgo(lastSavedAt) : null;

  return (
    <button
      type="button"
      onClick={onOpenHistory}
      data-testid={`studio-autosave-indicator-${state}`}
      className={`inline-flex items-center gap-1.5 rounded-md border px-2 py-1 text-xs font-medium transition-all hover:shadow-sm ${config.bg}`}
      title={t('studio.autosave.openHistory', { defaultValue: 'Open Save History' })}
    >
      {config.spin ? <Icon className="h-3 w-3 animate-spin" /> : <Icon className="h-3 w-3" />}
      <span data-testid="studio-autosave-label">{t(config.labelKey, { defaultValue: config.fallback })}</span>
      {state === 'saved' && timeAgo && (
        <span className="text-muted-foreground" data-testid="studio-autosave-timestamp">
          · {timeAgo}
        </span>
      )}
      {state === 'failed' && onRetry && (
        <span
          role="button"
          tabIndex={0}
          onClick={(e) => { e.stopPropagation(); onRetry(); }}
          onKeyDown={(e) => { if (e.key === 'Enter') { e.stopPropagation(); onRetry(); } }}
          className="ml-1 underline cursor-pointer"
          data-testid="studio-autosave-retry"
        >
          {t('studio.autosave.retry', { defaultValue: 'Retry' })}
        </span>
      )}
      {state === 'conflict' && (
        <span className="ml-1 italic" data-testid="studio-autosave-conflict-note">
          {t('studio.autosave.conflictHint', { defaultValue: 'Refresh to merge' })}
        </span>
      )}
    </button>
  );
}

const STATE_CONFIG = {
  idle:     { icon: Cloud,        spin: false, labelKey: 'studio.autosave.idle',     fallback: 'Up to date',     bg: 'bg-muted/30 border-border text-muted-foreground' },
  dirty:    { icon: Cloud,        spin: false, labelKey: 'studio.autosave.dirty',    fallback: 'Unsaved changes',bg: 'bg-amber-50 border-amber-300 text-amber-900 dark:bg-amber-500/15 dark:text-amber-200 dark:border-amber-500/40' },
  saving:   { icon: Loader2,      spin: true,  labelKey: 'studio.autosave.saving',   fallback: 'Saving…',        bg: 'bg-blue-50 border-blue-300 text-blue-900 dark:bg-blue-500/15 dark:text-blue-200 dark:border-blue-500/40' },
  saved:    { icon: CheckCircle2, spin: false, labelKey: 'studio.autosave.saved',    fallback: 'All changes saved', bg: 'bg-emerald-50 border-emerald-300 text-emerald-900 dark:bg-emerald-500/15 dark:text-emerald-200 dark:border-emerald-500/40' },
  failed:   { icon: AlertTriangle,spin: false, labelKey: 'studio.autosave.failed',   fallback: 'Save failed',    bg: 'bg-red-50 border-red-300 text-red-900 dark:bg-red-500/15 dark:text-red-200 dark:border-red-500/40' },
  conflict: { icon: CloudOff,     spin: false, labelKey: 'studio.autosave.conflict', fallback: 'Conflict',       bg: 'bg-orange-50 border-orange-300 text-orange-900 dark:bg-orange-500/15 dark:text-orange-200 dark:border-orange-500/40' },
};

/* ============================================================================
 *                    SAVE HISTORY SHEET (R1)
 * ========================================================================== */

export function SaveHistorySheet({ assetId, open, onOpenChange }) {
  const { t } = useTranslation();
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!open || !assetId) return;
    setLoading(true);
    adminApi.studioSaveHistory(assetId, 50)
      .then((res) => setRows(res.history || []))
      .catch((err) => toast.error(formatApiError(err)))
      .finally(() => setLoading(false));
  }, [assetId, open]);

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent side="right" className="w-full sm:max-w-md overflow-y-auto" data-testid="studio-save-history">
        <SheetHeader>
          <SheetTitle className="flex items-center gap-2">
            <History className="h-4 w-4" />
            {t('studio.saveHistory.title', { defaultValue: 'Save History' })}
          </SheetTitle>
        </SheetHeader>
        {loading ? (
          <div className="py-10 text-center">
            <Loader2 className="h-6 w-6 mx-auto animate-spin text-muted-foreground" />
          </div>
        ) : rows.length === 0 ? (
          <div className="py-10 text-center text-sm text-muted-foreground" data-testid="studio-save-history-empty">
            {t('studio.saveHistory.empty', { defaultValue: 'No saves yet. Start editing to see history here.' })}
          </div>
        ) : (
          <ul className="space-y-2 mt-4" data-testid="studio-save-history-list">
            {rows.map((r) => {
              const acts = r.actions || [];
              const hasSemantic = acts.length > 0;
              return (
                <li key={r.save_id || r.ts}>
                  <Card className="p-3" data-testid={`studio-save-history-row-${r.save_id || r.ts}`}>
                    <div className="flex items-start justify-between gap-2 mb-1">
                      <p className="text-xs text-muted-foreground">
                        {safeFmt(r.ts)} · {r.actor_role || '—'}
                      </p>
                      <Badge variant="outline" className="text-xs shrink-0">
                        {r.kind === 'manual' ? (
                          <Save className="h-2.5 w-2.5 mr-1" />
                        ) : (
                          <GitBranch className="h-2.5 w-2.5 mr-1" />
                        )}
                        {r.kind === 'manual'
                          ? t('studio.saveHistory.manual', { defaultValue: 'Manual' })
                          : t('studio.saveHistory.auto', { defaultValue: 'Auto' })}
                      </Badge>
                    </div>
                    {hasSemantic ? (
                      <ul className="space-y-0.5">
                        {acts.slice(0, 6).map((a, i) => (
                          <li key={i} className="text-xs flex items-start gap-1.5">
                            <span aria-hidden className="text-muted-foreground/60 mt-0.5">•</span>
                            <span data-testid={`studio-history-action-${a.code}`} className="leading-snug">
                              {a.label}
                            </span>
                          </li>
                        ))}
                        {acts.length > 6 && (
                          <li className="text-xs text-muted-foreground italic ml-3.5">
                            {t('studio.saveHistory.moreActions', { defaultValue: '+ {{n}} more', n: acts.length - 6 })}
                          </li>
                        )}
                      </ul>
                    ) : (
                      <p className="text-xs text-muted-foreground italic">{r.summary || '—'}</p>
                    )}
                  </Card>
                </li>
              );
            })}
          </ul>
        )}
        <p className="text-xs text-muted-foreground mt-4 italic">
          {t('studio.saveHistory.activityLogHint', { defaultValue: 'Activity Log — meaningful author actions instead of generic save summaries.' })}
        </p>
        <p className="text-xs text-muted-foreground mt-1 italic">
          {t('studio.saveHistory.snapshotsHint', { defaultValue: 'User-named snapshots arrive in Phase 2C.' })}
        </p>
      </SheetContent>
    </Sheet>
  );
}

/* ============================================================================
 *                    RECOVERY DIALOG (R2)
 * ========================================================================== */

export function RecoveryDialog({ open, snapshot, onRestore, onDiscard }) {
  const { t } = useTranslation();
  if (!open || !snapshot) return null;
  const ts = snapshot.last_dirty_at;
  return (
    <Dialog open={open} onOpenChange={() => { /* modal — explicit choice required */ }}>
      <DialogContent className="max-w-md" data-testid="studio-recovery-dialog" onPointerDownOutside={(e) => e.preventDefault()}>
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <RotateCcw className="h-4 w-4 text-primary" />
            {t('studio.recovery.title', { defaultValue: 'Restore unfinished session?' })}
          </DialogTitle>
        </DialogHeader>
        <p className="text-sm text-muted-foreground">
          {t('studio.recovery.body', {
            defaultValue: 'An unfinished editing session was found from {{when}}. Would you like to restore your previous work?',
            when: safeTimeAgo(ts) || t('studio.recovery.fallbackTime', { defaultValue: 'earlier' }),
          })}
        </p>
        <DialogFooter className="gap-2 mt-4">
          <Button
            variant="outline"
            onClick={onDiscard}
            data-testid="studio-recovery-discard"
          >
            <X className="h-4 w-4 mr-1.5" />
            {t('studio.recovery.discard', { defaultValue: 'Discard Changes' })}
          </Button>
          <Button
            onClick={onRestore}
            data-testid="studio-recovery-restore"
          >
            <RotateCcw className="h-4 w-4 mr-1.5" />
            {t('studio.recovery.restore', { defaultValue: 'Restore Session' })}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

/* ============================================================================
 *                    UNSAVED CHANGES DIALOG (R3)
 * ========================================================================== */

export function UnsavedChangesDialog({ open, onCancel, onConfirmLeave, onSaveAndLeave }) {
  const { t } = useTranslation();
  return (
    <Dialog open={open} onOpenChange={onCancel}>
      <DialogContent className="max-w-md" data-testid="studio-unsaved-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <AlertTriangle className="h-4 w-4 text-amber-500" />
            {t('studio.unsaved.title', { defaultValue: 'You have unsaved changes' })}
          </DialogTitle>
        </DialogHeader>
        <p className="text-sm text-muted-foreground">
          {t('studio.unsaved.body', { defaultValue: 'Your latest edits haven’t finished saving. What would you like to do?' })}
        </p>
        <DialogFooter className="gap-2 mt-4">
          <Button variant="ghost" onClick={onCancel} data-testid="studio-unsaved-stay">
            {t('studio.unsaved.stay', { defaultValue: 'Stay on page' })}
          </Button>
          <Button variant="outline" onClick={onConfirmLeave} data-testid="studio-unsaved-leave">
            {t('studio.unsaved.discard', { defaultValue: 'Leave anyway' })}
          </Button>
          {onSaveAndLeave && (
            <Button onClick={onSaveAndLeave} data-testid="studio-unsaved-save-and-leave">
              <Save className="h-4 w-4 mr-1.5" />
              {t('studio.unsaved.saveAndLeave', { defaultValue: 'Save & leave' })}
            </Button>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

/* ============================================================================
 *                    SESSION TIMER (R5)
 * ========================================================================== */

export function SessionTimer({ startedAt }) {
  const { t } = useTranslation();
  const [, force] = useState(0);
  useEffect(() => {
    const id = setInterval(() => force((n) => n + 1), 30_000); // refresh every 30s
    return () => clearInterval(id);
  }, []);
  const minutes = Math.max(0, Math.floor((Date.now() - startedAt) / 60_000));
  return (
    <span
      data-testid="studio-session-timer"
      data-session-minutes={minutes}
      className="inline-flex items-center gap-1 text-xs text-muted-foreground"
      title={t('studio.session.tooltip', { defaultValue: 'Editing session time' })}
    >
      <Timer className="h-3 w-3" />
      {minutes < 60
        ? t('studio.session.minutes', { defaultValue: '{{n}} min', n: minutes })
        : t('studio.session.hoursMinutes', { defaultValue: '{{h}}h {{m}}m', h: Math.floor(minutes / 60), m: minutes % 60 })}
    </span>
  );
}

/* -------------------------------------------------------------------------- */
function safeTimeAgo(iso) {
  if (!iso) return null;
  try {
    return formatDistanceToNow(new Date(iso), { addSuffix: true });
  } catch { return null; }
}
function safeFmt(iso) {
  if (!iso) return '—';
  try { return format(new Date(iso), 'MMM d · HH:mm'); } catch { return iso; }
}
